import React, { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { BarChart3 } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];

function buildSeries(rows, mode) {
  const sums = {};
  const dates = [];
  rows.filter((p) => p.status === "paid").forEach((p) => {
    const d = new Date(p.paid_at || p.created_at);
    dates.push(d);
    const k = mode === "day" ? dayKey(d) : monthKey(d);
    sums[k] = (sums[k] || 0) + (p.amount || 0);
  });
  if (!dates.length) return [];
  const min = new Date(Math.min(...dates));
  const max = new Date(Math.max(...dates));
  const out = [];
  if (mode === "day") {
    const cur = new Date(min.getFullYear(), min.getMonth(), min.getDate());
    if ((max - cur) / 864e5 > 92) {
      return Object.keys(sums).sort().map((k) => ({ label: k.slice(5).replace("-", "/"), amount: +sums[k].toFixed(2) }));
    }
    while (cur <= max) {
      const k = dayKey(cur);
      out.push({ label: `${pad(cur.getDate())}/${pad(cur.getMonth() + 1)}`, amount: +(sums[k] || 0).toFixed(2) });
      cur.setDate(cur.getDate() + 1);
    }
  } else {
    const cur = new Date(min.getFullYear(), min.getMonth(), 1);
    while (cur <= max) {
      const k = monthKey(cur);
      out.push({ label: `${MONTHS[cur.getMonth()]} ${cur.getFullYear()}`, amount: +(sums[k] || 0).toFixed(2) });
      cur.setMonth(cur.getMonth() + 1);
    }
  }
  return out;
}

export const PaymentsChart = ({ rows }) => {
  const [mode, setMode] = useState("day");
  const data = useMemo(() => buildSeries(rows, mode), [rows, mode]);

  return (
    <Card className="p-5" data-testid="payments-chart">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="font-bold flex items-center gap-2"><BarChart3 className="w-5 h-5 text-accent" /> المبالغ المدفوعة</h3>
        <div className="flex gap-1 p-1 rounded-full bg-muted">
          {[["day", "يومي"], ["month", "شهري"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={mode === k ? "default" : "ghost"} className="rounded-full h-8 px-4" onClick={() => setMode(k)} data-testid={`payments-chart-${k}`}>{l}</Button>
          ))}
        </div>
      </div>
      {data.length === 0 ? (
        <div className="h-[240px] flex items-center justify-center text-sm text-muted-foreground" data-testid="payments-chart-empty">لا توجد مبالغ مدفوعة في هذه الفترة</div>
      ) : (
        <div dir="ltr">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fontFamily: "IBM Plex Sans Arabic" }} reversed />
              <YAxis orientation="right" tick={{ fontSize: 11 }} width={56} />
              <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} ر.س`, "المدفوع"]} contentStyle={{ fontFamily: "IBM Plex Sans Arabic", borderRadius: 12, direction: "rtl" }} />
              <Bar dataKey="amount" fill="hsl(var(--accent))" radius={[8, 8, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
};
